import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { atmosphereFor } from "@/lib/studio";

// One realistic cinematic background per page, with 3D parallax + VFX layers.
export default function CinematicBackground() {
  const { pathname } = useLocation();
  const atmo = useMemo(() => atmosphereFor(pathname), [pathname]);
  const [p, setP] = useState({ x: 0, y: 0 });
  const reduced = useRef(false);

  useEffect(() => {
    reduced.current = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced.current) return;
    let raf = 0;
    const onMove = (e) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const x = (e.clientX / window.innerWidth - 0.5) * 2;
        const y = (e.clientY / window.innerHeight - 0.5) * 2;
        setP({ x, y });
      });
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => { window.removeEventListener("mousemove", onMove); cancelAnimationFrame(raf); };
  }, []);

  const dust = useMemo(
    () => Array.from({ length: 16 }).map(() => ({
      left: `${Math.random() * 100}%`,
      delay: `${-Math.random() * 24}s`,
      dur: `${16 + Math.random() * 20}s`,
      scale: 0.5 + Math.random() * 1.5,
    })), []
  );

  return (
    <div className="cine-world" aria-hidden data-testid="cinematic-background">
      {/* Sharp cinematic world — crossfade between rooms, parallax depth, slow living camera */}
      <AnimatePresence mode="sync">
        <motion.div
          key={atmo.image}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.3, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          {/* parallax layer follows the pointer for 3D depth */}
          <div
            className="cine-parallax"
            style={{ transform: `translate3d(${p.x * -14}px, ${p.y * -14}px, 0)` }}
          >
            {/* the crisp, un-blurred artwork with a slow ken-burns drift */}
            <div
              className="cine-world-img"
              style={{ backgroundImage: `url("${atmo.image}")` }}
            />
          </div>
        </motion.div>
      </AnimatePresence>

      {/* room tint */}
      <div className="absolute inset-0" style={{ background: `radial-gradient(90% 70% at 30% 30%, ${atmo.tint}, transparent 70%)` }} />

      {/* VFX: projector beams + haze */}
      <div className="cine-beam b1" />
      <div className="cine-beam b2" />
      <div className="cine-haze" />

      {/* VFX: floating embers/dust */}
      {dust.map((d, i) => (
        <span key={i} className="cine-dust"
          style={{ left: d.left, animationDelay: d.delay, animationDuration: d.dur, transform: `scale(${d.scale})` }} />
      ))}

      <div className="cine-world-scrim" />
    </div>
  );
}
